import * as analyticsFunctions from '@agoda/analytics-data-acquisition/dist/functions/data';
import { ContextFieldsMap } from '@agoda/analytics-data-acquisition/dist/core';
import { BootstrapConfig, Geo, MobileDeviceType } from 'core/type/BootstrapConfig.types';
import { ActivitiesWebAppDataContextFieldsV51, ActivitiesWebAppDataContextIdsV51 } from '@agoda/analytics-messages-definition-activities/applications.split';
import { getWindowLocation } from 'core/window/windowService';
import { WebAnalyticsContextBinder, WebAnalyticsObserver } from '@agoda/analytics-data-acquisition';
import { isB } from 'core/window/bootstrapConfig';
import { ExperimentId } from 'core/type/ExperimentId';

const AnalyticsSessionExpiry = () => analyticsFunctions.analyticsSessionExpiry();
const AnalyticsSessionId = () => analyticsFunctions.analyticsSessionId();
const AnalyticsSessionSignature = () => analyticsFunctions.analyticsSessionSignature();
const IframeParentHost = () => analyticsFunctions.iframeParentHost();
const IsIframe = () => analyticsFunctions.isIframe();
const IsIncognito = () => false;
const PageVisitId = () => analyticsFunctions.pageVisitId();
const ReferrerUrl = () => analyticsFunctions.referrerUrlFunc();
const RequestUrl = () => getWindowLocation().href;
const ScreenHeight = () => analyticsFunctions.screenHeight();
const ScreenWidth = () => analyticsFunctions.screenWidth();

// Re Export Type to reduce the impact of changes in AppDataContext.
// The AppDataContext would change frequently when we add/remove fields from context.
export type ActivitiesWebAppDataContextFields = ActivitiesWebAppDataContextFieldsV51;
export type PartialActivitiesWebAppDataContextFields = Partial<ActivitiesWebAppDataContextFieldsV51>;

export type WebAnalyticsBinder = (webAnalyticsBinder: WebAnalyticsContextBinder & WebAnalyticsObserver, config: BootstrapConfig) => void;
export declare type WebAnalyticsContextFieldsMap<TFields> = {
    [P in keyof TFields]: TFields[P] | (() => TFields[P] | undefined);
};

type AnalyticsCurrency = ActivitiesWebAppDataContextFields['currency'];
type AnalyticsLanguage = ActivitiesWebAppDataContextFields['language'];

const createActivitiesWebAnalyticsBinder = (
    appType: ActivitiesWebAppDataContextFields['app_type'],
    config: BootstrapConfig,
    fieldsMap?: Partial<ContextFieldsMap<ActivitiesWebAppDataContextFields>>,
): WebAnalyticsBinder => {
    const geo = config.geo || ({} as Geo);
    return (webAnalytics): void => {
        webAnalytics.appContext<ActivitiesWebAppDataContextIdsV51, ActivitiesWebAppDataContextFields>(
            {
                app_data_version: 51,
                app_data: 'activities',
            },
            {
                app_type: appType,
                is_webview: config.isWebView,
                is_internal_user: config.isInternal,
                analytics_session_expiry: AnalyticsSessionExpiry,
                analytics_session_id: AnalyticsSessionId,
                analytics_session_signature: AnalyticsSessionSignature,
                channel_id: config.pageConfig.cid || -1,
                city_id: geo.cityId,
                country_code_iso2: geo.countryISO2 && geo.countryISO2.toUpperCase(),
                search_country_id: geo.countryId,
                currency: config.currency.code.toLocaleLowerCase() as AnalyticsCurrency,
                iframe_parent_host: IframeParentHost,
                is_iframe: IsIframe,
                is_incognito: IsIncognito,
                language: config.language.locale as AnalyticsLanguage,
                logged_in: (config.pageConfig && config.pageConfig.loginLvl) || 0,
                member_id: config.isLoggedIn ? config.memberId : undefined,
                page_visit_id: PageVisitId(),
                referrer_url: ReferrerUrl(),
                request_url: RequestUrl(),
                screen_height: ScreenHeight,
                screen_width: ScreenWidth,
                session_id: (config.sessionId && config.sessionId.toString()) || '',
                user_id: config.pageConfig.userId || '',
                whitelabelid: config.pageConfig.whitelabelid || -1,
                ...fieldsMap,
            },
        );
    };
};

export const getAppType = (config: BootstrapConfig): ActivitiesWebAppDataContextFields['app_type'] => {
    const { isWebView, browser, userDevice, experiments } = config;
    const { isMobile } = browser;
    const { mobileDeviceType } = userDevice;

    if (isWebView && isMobile) {
        if (isB(ExperimentId.ACT_4630, experiments) && mobileDeviceType === MobileDeviceType.iOS) {
            return 'ios';
        }
        if (isB(ExperimentId.ACT_4922, experiments) && mobileDeviceType === MobileDeviceType.Android) {
            return 'android';
        }
    }

    return isMobile ? 'mob-web' : 'desk-web';
};

const getActivitiesAppContext = (config: BootstrapConfig, fieldsMap?: Partial<ContextFieldsMap<ActivitiesWebAppDataContextFields>>): WebAnalyticsBinder => {
    const appType = getAppType(config);
    return createActivitiesWebAnalyticsBinder(appType, config, fieldsMap);
};

export default getActivitiesAppContext;
